import { Children, FC } from "react";
import { Repeater, Text, types } from "react-bricks/frontend";
import styled from "styled-components";
import { color, fonts } from "@styles";
import { Tooltip } from "components/atoms/Tooltip";
import { StatsBarItemProps } from "./StatsBarItem";

enum Background {
  blue = "blue",
  purple = "purple",
  gray = "gray",
}

export interface StatsBarProps {
  background?: Background;
  tooltipCopy?: string;
  hasHeading?: boolean;
  stats: types.RepeaterItems<StatsBarItemProps>;
  hasStats?: boolean;
  bodyTextSize?: string;
}

export const StatsBar: types.Brick<StatsBarProps> = ({
  background = Background.blue,
  tooltipCopy,
  stats = [],
  hasHeading,
  hasStats,
  bodyTextSize,
}) => {
  const { backgroundColor, inverted, textColor, labelColor, titleColor } =
    getPropsFromBackground(background);
  const customBreakpoint = "65rem";
  return (
    <Wrapper backgroundColor={backgroundColor} customBreakpoint={customBreakpoint}>
      {hasHeading && (
        <HeadingSection customBreakpoint={customBreakpoint}>
          <HeadingContainer>
            <Text
              propName="heading"
              placeholder="Enter heading here"
              renderBlock={({ children }) => (
                <HeadingTitle color={titleColor}>
                  {children}
                  {tooltipCopy && (
                    <ToolTipWrap>
                      <Tooltip inverted={inverted} tooltipDesktopWidth={"18.75rem"} isOnHeading>
                        {tooltipCopy}
                      </Tooltip>
                    </ToolTipWrap>
                  )}
                </HeadingTitle>
              )}
              renderPlaceholder={({ children }) => <div id="placeholder">{children}</div>}
            />
          </HeadingContainer>
        </HeadingSection>
      )}
      <StatContainer customBreakpoint={customBreakpoint}>
        <Repeater
          propName="stats"
          items={stats}
          itemProps={{ statColor: textColor, textColor: labelColor, hasStats, bodyTextSize }}
          renderItemWrapper={(item, i) => (
            <>
              {i > 0 && (
                <Line
                  inverted={inverted}
                  customBreakpoint={customBreakpoint}
                  height={hasStats ? "4.125rem" : "2rem"}
                />
              )}
              {item}
            </>
          )}
        />
      </StatContainer>
    </Wrapper>
  );
};

const getPropsFromBackground = (c: Background) => {
  switch (c) {
    case Background.blue:
      return {
        backgroundColor: color.S2100,
        titleColor: color.white,
        textColor: color.white,
        labelColor: color.white,
        inverted: false,
      };
    case Background.purple:
      return {
        backgroundColor: color.S3100,
        titleColor: color.white,
        textColor: color.white,
        labelColor: color.white,
        inverted: false,
      };
    case Background.gray:
      return {
        backgroundColor: color.B210,
        titleColor: color.inkHeadline,
        textColor: color.yulifePink.P100,
        labelColor: color.B1100,
        inverted: true,
      };
  }
};

StatsBar.schema = {
  name: "stats-bar",
  label: "Stats Bar",
  category: "Stats Components",
  previewImageUrl: "/static/bricks/stats-bar.png",
  repeaterItems: [
    {
      name: "stats",
      itemType: "stats-bar-block",
      itemLabel: "Stat",
      min: 1,
      max: 3,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    heading: "The impact of YuLife insurance and wellbeing",
    tooltipCopy: "",
    color: Background.blue,
    hasHeading: true,
    hasStats: true,
    stats: [
      {
        value: "5x",
        description: "increase in steps meditation",
        bold: false,
        tooltip: "",
      },
      {
        value: "85%",
        description: "of employees say YuLife engages them better than any other benefit",
        bold: false,
        tooltip: "",
      },
      {
        value: "3x",
        description: "increase in engagement to EAP and VGP",
        bold: false,
        tooltip: "",
      },
    ],
  }),
  // Define side edit props
  sideEditProps: [
    {
      name: "background",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: Object.values(Background).map((value) => ({ value, label: value })),
      },
    },
    {
      name: "hasHeading",
      label: "Has Heading?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "tooltipCopy",
      label: "Tooltip Copy",
      type: types.SideEditPropType.Text,
      shouldRefreshStyles: true,
      show: (props) => props.hasHeading,
    },
    {
      name: "hasStats",
      label: "Has Stats?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "bodyTextSize",
      label: "Body Text Size",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: [
          {
            label: "Small",
            value: "1rem",
          },
          {
            label: "Medium",
            value: "1.2rem",
          },
          {
            label: "Large",
            value: "1.4rem",
          },
        ],
      },
    },
  ],
};

const HeadingTitle = styled.h2<{ color: string }>`
  margin-top: 0;
  color: ${(p) => p.color};
  font-family: ${fonts.heading};
  font-size: 2rem;
  line-height: 2.5rem;
  letter-spacing: 0rem;
  text-align: center;
  margin: 0;
`;

const HeadingSection = styled.div<{ customBreakpoint: string }>`
  margin: 0;
  display: flex;
  justify-content: center;
  padding-bottom: 2.5rem;

  @media only screen and (max-width: ${(p) => p.customBreakpoint}) {
    padding-top: 5rem;
  }
`;

const Wrapper = styled.section<{
  backgroundColor: string;
  customBreakpoint: string;
}>`
  background: ${(p) => p.backgroundColor || color.S2100};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;

  @media only screen and (max-width: ${(p) => p.customBreakpoint}) {
    padding: 0;
  }
`;

const StatContainer = styled.div<{ customBreakpoint: string }>`
  justify-items: center;
  align-items: center;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  margin: 0 auto;
  overflow: visible;

  @media only screen and (max-width: ${(p) => p.customBreakpoint}) {
    flex-direction: column;
    padding: 0 0 5rem;
  }
`;

const Line = styled.div<{ inverted: boolean; height: string; customBreakpoint: string }>`
  height: ${(p) => p.height};
  border-left: ${(p) => (p.inverted ? `1px solid ${color.B240}` : "1px solid white")};

  @media only screen and (max-width: ${(p) => p.customBreakpoint}) {
    height: 1px;
    border-left: none;
    border-bottom: ${(p) => (p.inverted ? `1px solid ${color.B240}` : "1px solid white")};
    width: 50%;
    padding: 1rem 0;
  }
`;

const ToolTipWrap = styled.span`
  margin-top: 0.5rem;
  position: relative;
  color: ${color.B1100};
  font-family: ${fonts.body};
`;

const HeadingContainer = styled.span`
  padding: 0rem;
`;

export default StatsBar;
